import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { authService, User } from '../services/authService';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  updateUser: (userData: Partial<User>) => void;
  isAuthenticated: boolean;
  hasPermission: (permission: string) => boolean;
  hasAnyPermission: (permissions: string[]) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        console.log('AuthContext: Initializing auth, checking if authenticated...');
        if (authService.isAuthenticated()) {
          console.log('AuthContext: Found tokens, getting current user...');
          const currentUser = await authService.getCurrentUser();
          console.log('AuthContext: Got current user:', currentUser.username);
          setUser(currentUser);
        } else {
          console.log('AuthContext: No tokens found, user remains null');
        }
      } catch (error) {
        console.error('AuthContext: Failed to initialize auth:', error);
        authService.clearTokens();
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();
  }, []);

  const login = async (username: string, password: string) => {
    setLoading(true);
    console.log('AuthContext: Starting login for:', username);
    try {
      await authService.login({ username, password });
      console.log('AuthContext: Login API call successful, getting current user...');
      const currentUser = await authService.getCurrentUser();
      console.log('AuthContext: Setting user:', currentUser.username);
      setUser(currentUser);
    } catch (error) {
      console.log('AuthContext: Login failed, clearing user state');
      // Clear user state on login failure
      setUser(null);
      // Re-throw the error so the Login component can handle it
      throw error;
    } finally {
      console.log('AuthContext: Login process finished, setting loading to false');
      setLoading(false);
    }
  };

  const logout = async () => {
    await authService.logout();
    setUser(null);
  };

  const updateUser = (userData: Partial<User>) => {
    if (user) {
      setUser({ ...user, ...userData });
    }
  };

  const hasPermission = (permission: string): boolean => {
    return authService.hasPermission(permission, user || undefined);
  };

  const hasAnyPermission = (permissions: string[]): boolean => {
    return authService.hasAnyPermission(permissions, user || undefined);
  };

  const isAuthenticated = !!user;
  
  // Debug logging for authentication state changes
  useEffect(() => {
    console.log('AuthContext: Authentication state changed - user:', user?.username || 'null', 'isAuthenticated:', isAuthenticated);
  }, [user, isAuthenticated]);

  const value: AuthContextType = {
    user,
    loading,
    login,
    logout,
    updateUser,
    isAuthenticated,
    hasPermission,
    hasAnyPermission,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};